<script setup>
import { useTemplateRef, onMounted, onUnmounted, watch } from 'vue'
import { Map, View } from 'ol'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import { fromLonLat, transform as transformOL } from 'ol/proj'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import { Icon, Style } from 'ol/style'
import { registerProjection } from '@/services/map/ProjectionService'

const { coordinates = [24.7536, 59.437], zoom = 16, projection = 'EPSG:4326' } = defineProps({
  coordinates: Array,
  zoom: Number,
  projection: String
})

const mapContainer = useTemplateRef('mapContainer')
let map = null
let markerSource = null

// Custom branded map marker SVG with blue gradient
const customMarkerSvg = `
<svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="markerGradient" x1="0" y1="0" x2="32" y2="40" gradientUnits="userSpaceOnUse">
      <stop stop-color="#3B82F6"/>
      <stop offset="1" stop-color="#1D4ED8"/>
    </linearGradient>
    <filter id="markerShadow" x="-20%" y="-10%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.25"/>
    </filter>
  </defs>
  <g filter="url(#markerShadow)">
    <path d="M16 2C8.268 2 2 8.268 2 16c0 10 14 22 14 22s14-12 14-22c0-7.732-6.268-14-14-14z" fill="url(#markerGradient)"/>
    <circle cx="16" cy="14" r="5" fill="white"/>
  </g>
</svg>
`

const getMapCoords = (coords, proj) => {
  if (proj === 'EPSG:3301') {
    registerProjection('EPSG:3301')
    return transformOL([coords[0], coords[1]], 'EPSG:3301', 'EPSG:3857')
  }
  return fromLonLat(coords)
}

const createMarkerStyle = () => {
  return new Style({
    image: new Icon({
      anchor: [0.5, 1],
      src: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(customMarkerSvg),
      scale: 1.1
    })
  })
}

const initMap = () => {
  if (!mapContainer.value) return

  const mapCoords = getMapCoords(coordinates, projection)

  markerSource = new VectorSource()

  const markerFeature = new Feature({
    geometry: new Point(mapCoords)
  })

  markerFeature.setStyle(createMarkerStyle())
  markerSource.addFeature(markerFeature)

  map = new Map({
    target: mapContainer.value,
    layers: [
      new TileLayer({
        source: new OSM()
      }),
      new VectorLayer({
        source: markerSource
      })
    ],
    view: new View({
      center: mapCoords,
      zoom: zoom,
      maxZoom: 19
    }),
    controls: []
  })
}

watch(() => coordinates, (newCoords) => {
  if (map && newCoords) {
    const mapCoords = getMapCoords(newCoords, projection)
    map.getView().animate({ center: mapCoords, duration: 500 })
    markerSource.clear()
    const newMarker = new Feature({
      geometry: new Point(mapCoords)
    })
    newMarker.setStyle(createMarkerStyle())
    markerSource.addFeature(newMarker)
  }
}, { deep: true })

onMounted(() => {
  initMap()
})

onUnmounted(() => {
  if (map) {
    map.dispose()
  }
})
</script>

<template>
  <div class="address-preview-map overflow-hidden rounded-xl border border-slate-200 shadow-md h-36 sm:h-44 w-full relative ring-1 ring-black/5">
    <div ref="mapContainer" class="w-full h-full"></div>
    <!-- Subtle overlay to make it look non-interactive -->
    <div class="absolute inset-0 bg-transparent cursor-default pointer-events-none"></div>
  </div>
</template>

<style scoped>
:deep(.ol-viewport) {
  border-radius: 0.75rem;
}
</style>
